import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { Nullable } from '@clearverify/types';

import {
  DisclaimerInfoFragment,
  DisclaimersListDocument,
  useDisclaimerCreateMutation,
} from 'src/api';
import { AutocompleteField, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { DisclaimerEditFormFields, DisclaimerEditFormValues } from './types';
import { getDisclaimerEditValidationSchema } from './utils';

interface DisclaimerEditFormProps {
  closeModal: () => void;
  employerId: Nullable<string>;
  disclaimersList: Nullable<DisclaimerInfoFragment[]>;
}

export const DisclaimerCreateForm: React.FC<DisclaimerEditFormProps> = ({
  closeModal,
  employerId,
  disclaimersList,
}) => {
  const { createApolloContext } = useNotification();

  const [createDisclaimer] = useDisclaimerCreateMutation({
    refetchQueries: [DisclaimersListDocument],
    context: createApolloContext({ successMessage: 'Disclaimer successfully created' }),
  });

  const initialValues: DisclaimerEditFormValues = {
    [DisclaimerEditFormFields.name]: '',
    [DisclaimerEditFormFields.disclaimer]: '',
    [DisclaimerEditFormFields.titles]: [],
  };

  const handleCreate = React.useCallback(
    async (formData: DisclaimerEditFormValues) => {
      if (employerId) {
        try {
          await createDisclaimer({
            variables: {
              data: {
                name: formData.name,
                disclaimer: formData.disclaimer,
                titles: formData.titles.filter(isNotNil),
                employer: {
                  connect: {
                    id: employerId,
                  },
                },
              },
            },
          });
          closeModal();
        } catch (error) {
          console.error(error);
        }
      }
    },
    [closeModal, createDisclaimer, employerId],
  );

  return (
    <Form
      onSubmit={handleCreate}
      initialValues={initialValues}
      subscription={{ valid: true, submitting: true }}
      keepDirtyOnReinitialize
      validate={validateWithYupSchema(getDisclaimerEditValidationSchema(disclaimersList ?? []))}
    >
      {({ submitting, valid }) => {
        return (
          <React.Fragment>
            <Grid container spacing={2} mb={4}>
              <Grid size={12}>
                <Field
                  label="Disclaimer name"
                  name={DisclaimerEditFormFields.name}
                  fullWidth
                  required
                  disabled={submitting}
                  component={TextField}
                />
              </Grid>

              <Grid size={12}>
                <Field
                  label="Titles"
                  name={DisclaimerEditFormFields.titles}
                  placeholder="Type Position title, then press “enter”"
                  fullWidth
                  required
                  disabled={submitting}
                  freeSolo
                  multiple
                  component={AutocompleteField}
                  options={[]}
                />
                <Typography sx={{ marginLeft: '12px' }} variant="chip" color="GrayText">
                  To add several Titles, press “enter” after each title.
                </Typography>
              </Grid>

              <Grid size={12}>
                <Typography mb={2} variant="subtitle1">
                  Employer Disclaimer
                </Typography>
                <Field
                  label="Disclaimer"
                  name={DisclaimerEditFormFields.disclaimer}
                  fullWidth
                  multiline
                  required
                  minRows={3}
                  disabled={submitting}
                  component={TextField}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="center" gap={2} mt={2}>
              <Button variant="outlined" color="error" onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting}
                loading={submitting}
              >
                Save changes
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};
